import React, { useState, useEffect, useCallback } from 'react';
import PFCHeader from './PFCHeaderComponent';
import PFCFooter from './PFCFooterComponent';
import useDataStore from '../../store/dataStore';
import { getBaseUrl } from '../../helpers/addUserWithotPayment';
import '../../assets/css/pfc.css';
import { VALID_RELATIONSHIPS } from './consentJourneyConstants';

const ShieldIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path d="M5.5 1L10 3V5.5C10 7.9 8 10 5.5 11C3 10 1 7.9 1 5.5V3L5.5 1Z" stroke="rgba(255,255,255,.8)" strokeWidth="1.1" fill="none" />
  </svg>
);

const CheckIcon = ({ color }) => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <path d="M1 4L3 6L7 2" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const HeroFeature = ({ icon, title, sub }) => (
  <div className="pfc-hero-feature">
    <div className="pfc-hero-feature-icon">{icon}</div>
    <div>
      <div className="pfc-hero-feature-title">{title}</div>
      <div className="pfc-hero-feature-sub">{sub}</div>
    </div>
  </div>
);

const InputField = ({ label, name, value, onChange, onBlur, error, type, badge, prefix, placeholder, maxLength, fullWidth, max }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : undefined}>
    <label className="pfc-field-label" htmlFor={`pfc-field-${name}`} dangerouslySetInnerHTML={{ __html: label }} />
    <div className={`pfc-input ${badge === 'select' ? 'select' : ''} ${type === 'date' ? 'date-field' : ''} ${error ? 'error' : ''}`}>
      {prefix && <span className="pfc-input-prefix">{prefix}</span>}
      <input
        id={`pfc-field-${name}`}
        type={type || 'text'}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        max={max}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `pfc-field-${name}-error` : undefined}
        style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: 14, fontFamily: 'inherit', color: '#252D38', width: '100%' }}
      />
      {badge === 'select' && <span className="pfc-input-arrow">&#9660;</span>}
    </div>
    {error && <div id={`pfc-field-${name}-error`} className="pfc-field-error" role="alert">{error}</div>}
  </div>
);

const SelectField = ({ label, name, value, onChange, onBlur, error, options, placeholder, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : undefined}>
    <label className="pfc-field-label" htmlFor={`pfc-field-${name}`} dangerouslySetInnerHTML={{ __html: label }} />
    <div className={`pfc-input select ${error ? 'error' : ''}`}>
      <select
        id={`pfc-field-${name}`}
        name={name}
        value={value || ''}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `pfc-field-${name}-error` : undefined}
        style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: 14, fontFamily: 'inherit', color: value ? '#252D38' : '#8A99AD', width: '100%', appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}
      >
        <option value="" disabled>{placeholder || 'Select'}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <span className="pfc-input-arrow">&#9660;</span>
    </div>
    {error && <div id={`pfc-field-${name}-error`} className="pfc-field-error" role="alert">{error}</div>}
  </div>
);

const BRANCH_LOCATIONS = ['Bangalore Main Branch', 'Mumbai HQ', 'Delhi NCR', 'Chennai', 'Hyderabad', 'Pune'];
const VERTICAL_DEPARTMENTS = ['Retail Loans', 'Home Loans', 'Business Loans', 'Personal Loans'];
const SOURCING_PARTNERS = ['Inhouse', 'BMap', 'PACE'];
const NEW_BUSINESS_OPTIONS = ['Yes', 'No'];
const OPTIONAL_AGENT_FIELDS = ['sourcingPartner', 'newBusiness'];
const NAME_FIELDS = ['firstName', 'lastName', 'nomineeName', 'guardianName', 'agentName'];

const DobField = ({ label, name, value, onChange, onBlur, error }) => (
  <InputField label={label} name={name} value={value} onChange={onChange} onBlur={onBlur} error={error} type="date" max={todayStr()} />
);

const NextStep = ({ text }) => (
  <div className="pfc-next-step">
    <div className="pfc-next-step-icon">
      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
        <path d="M1 4L3 6L7 2" stroke="#26935F" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    {text}
  </div>
);

const formatPrice = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN');

const todayStr = () => new Date().toISOString().split('T')[0];

const calcAge = (dobStr) => {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return null;
  const diff = Date.now() - dob.getTime();
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
};

function getMembers(pkg) {
  if (!pkg.complexStructure) return pkg.members || 1;
  if (pkg.structure && pkg.structure.length > 0) return pkg.structure[0].members;
  return 1;
}

function getPrice(pkg) {
  if (!pkg.complexStructure) return pkg.price || 0;
  if (pkg.structure && pkg.structure.length > 0) return pkg.structure[0].price;
  return 0;
}

function getFeatures(pkg) {
  return pkg.content || [];
}

function PFCLandingComponent() {
  const configData = useDataStore((state) => state.data);
  const packages = (configData && configData.packages) || [];

  const [screen, setScreen] = useState('home');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [completedTabs, setCompletedTabs] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submissionSummary, setSubmissionSummary] = useState(null);

  const nomineeAge = calcAge(formData.nomineeDob);
  const isNomineeMinor = nomineeAge !== null && nomineeAge < 18;

  const validateField = (name, value) => {
    if (OPTIONAL_AGENT_FIELDS.includes(name) && (!value || !value.trim())) return '';
    if (!value || !value.trim()) return 'This field is required';
    switch (name) {
      case 'firstName':
      case 'lastName':
      case 'nomineeName':
      case 'guardianName':
      case 'agentName':
        if (!/^[A-Za-z\s]+$/.test(value.trim())) return 'Enter a valid name. Only alphabets and spaces are allowed';
        break;
      case 'email':
      case 'agentEmail':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address';
        break;
      case 'mobile':
      case 'agentMobile':
        if (!/^[6-9]\d{9}$/.test(value)) return 'Enter a valid 10-digit mobile number';
        break;
      case 'pincode':
        if (!/^[1-9]\d{5}$/.test(value)) return 'Enter a valid 6-digit pincode';
        break;
      case 'dob':
      case 'nomineeDob':
        if (isNaN(new Date(value).getTime())) return 'Enter a valid date';
        if (new Date(value) > new Date()) return 'Date cannot be in the future';
        break;
      case 'lan':
        if (value.trim().length < 5) return 'Enter a valid Loan Account Number';
        break;
      default:
        break;
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = NAME_FIELDS.includes(name) ? value.replace(/[^A-Za-z\s]/g, '') : value;
    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const err = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: err }));
  };

  const validateTab = (tab) => {
    let tabFields = {
      0: ['firstName', 'lastName', 'dob', 'gender', 'lan', 'mobile', 'email', 'city', 'state', 'pincode', 'address'],
      1: ['nomineeName', 'nomineeRelation', 'nomineeDob'],
      2: ['agentName', 'agentEmail', 'agentCode', 'agentMobile', 'branchLocation', 'verticalDepartment'],
    };
    if (tab === 1 && isNomineeMinor) {
      tabFields[1] = tabFields[1].concat(['guardianName', 'guardianRelation', 'guardianMobile']);
    }
    const fields = tabFields[tab] || [];
    const newErrors = {};
    let hasError = false;
    fields.forEach((f) => {
      const err = validateField(f, formData[f] || '');
      if (err) {
        newErrors[f] = err;
        hasError = true;
      }
    });
    if (hasError) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
    } else {
      setCompletedTabs((prev) => ({ ...prev, [tab]: true }));
    }
    return !hasError;
  };

  const getAllRequiredFields = () => {
    let fields = ['firstName', 'lastName', 'dob', 'gender', 'lan', 'mobile', 'email', 'city', 'state', 'pincode', 'address', 'nomineeName', 'nomineeRelation', 'nomineeDob', 'agentName', 'agentEmail', 'agentCode', 'agentMobile', 'branchLocation', 'verticalDepartment'];
    if (isNomineeMinor) {
      fields = fields.concat(['guardianName', 'guardianRelation', 'guardianMobile']);
    }
    return fields;
  };

  const isAllFieldsValid = () => {
    const fields = getAllRequiredFields();
    return fields.every((f) => {
      const val = formData[f] || '';
      if (!val.trim()) return false;
      return !validateField(f, val);
    });
  };

  const allValid = isAllFieldsValid();

  const goToTab = (tab) => {
    setActiveTab(tab);
    window.scrollTo(0, 0);
  };

  const handleTabClick = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    window.scrollTo(0, 0);
  };

  const handleTabKeyDown = (event, tab) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const nextTab = event.key === 'ArrowRight' ? (tab + 1) % 3 : (tab + 2) % 3;
    handleTabClick(nextTab);
    document.getElementById(`pfc-tab-${nextTab}`)?.focus();
  };

  const heroTitle = (configData && configData.pfcHeroTitle) || 'MediBuddy Membership Plans';
  const heroSubtitle = (configData && configData.pfcHeroSubtitle) || 'Click on any plan to fill customer details and generate a payment link';
  const footerNote = (configData && configData.pfcFooterNote) || 'Products exclusively designed for Poonawalla Fincorp';

  // Sync internal screen changes with browser history so back button works
  const goToScreen = useCallback((next) => {
    setScreen(next);
    window.scrollTo(0, 0);
  }, []);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setSubmissionSummary(null);
    setActiveTab(0);
    setFormData({});
    setErrors({});
    setCompletedTabs({});
    goToScreen('enrollment');
    window.history.pushState({ pfcScreen: 'enrollment' }, '');
  };

  const handleBackToPlans = () => {
    goToScreen('home');
    window.history.back();
  };

  const handleSendConsent = async () => {
    if (!allValid) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const price = getPrice(selectedPlan);
      const genderMap = { male: 'Male', female: 'Female', other: 'Other' };
      const payload = {
        name: `${formData.firstName || ''} ${formData.lastName || ''}`.trim(),
        mobile: formData.mobile || '',
        packageId: selectedPlan.id,
        source: 'CreateAffinity',
        email: formData.email || undefined,
        amount: price || undefined,
        dob: formData.dob || undefined,
        gender: genderMap[formData.gender] || undefined,
        loan_acct_number: formData.lan || undefined,
        address: formData.address || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        pincode: formData.pincode || undefined,
        payment_mode: 'landingPage',
      };
      if (selectedPlan.master_policy_no) {
        payload.master_policy_no = selectedPlan.master_policy_no;
      }
      if (formData.nomineeName) {
        payload.nominee = {
          name: formData.nomineeName,
          relationship: formData.nomineeRelation || undefined,
          dob: formData.nomineeDob || undefined,
        };
      }
      if (isNomineeMinor && formData.guardianName) {
        payload.guardian = {
          name: formData.guardianName,
          relationship: formData.guardianRelation || undefined,
          phone: formData.guardianMobile || undefined,
        };
      }
      const extraInfo = {};
      if (formData.agentName) {
        extraInfo.agentDetails = {
          name: formData.agentName,
          email: formData.agentEmail || undefined,
          code: formData.agentCode || undefined,
          phone: formData.agentMobile || undefined,
          branchLocation: formData.branchLocation || undefined,
          verticalDepartment: formData.verticalDepartment || undefined,
          sourcingPartner: formData.sourcingPartner || undefined,
          newBusiness: formData.newBusiness || undefined,
        };
      }
      if (Object.keys(extraInfo).length > 0) {
        payload.extraInfo = extraInfo;
      }

      const res = await fetch(`${getBaseUrl()}/patientswebapp/affinity/createPartnerUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.success !== 1) {
        setSubmitError(data.error || 'Failed to send consent request. Please try again.');
        setSubmitting(false);
        return;
      }
      setSubmissionSummary({
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        mobile: formData.mobile || '',
      });
      setFormData({});
      setErrors({});
      setCompletedTabs({});
      setActiveTab(0);
      setSelectedPlan(null);
      setSubmitError('');
      setSubmitting(false);
      window.history.replaceState({ pfcScreen: 'home' }, '');
      goToScreen('consent');
      window.history.pushState({ pfcScreen: 'consent' }, '');
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handleGoHome = () => {
    window.history.back();
  };

  // Listen for browser back/forward to navigate between internal screens
  useEffect(() => {
    const handlePopState = (e) => {
      if (!e.state || !e.state.pfcScreen) {
        setScreen('home');
      } else {
        setScreen(e.state.pfcScreen);
      }
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /* ── Screen 1: Home (plan selection) ── */
  if (screen === 'home') {
    return (
      <div className="pfc-portal">
        <PFCHeader />

        <div className="pfc-hero">
          <div className="pfc-hero-circle" />
          <div className="pfc-hero-content">
            <div className="pfc-hero-badge">
              <ShieldIcon /> Exclusively for Poonawalla Fincorp Customers
            </div>
            <div className="pfc-hero-title">{heroTitle}</div>
            <div className="pfc-hero-subtitle">{heroSubtitle}</div>
            <div className="pfc-hero-features">
              <HeroFeature
                icon={<svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1L11 3.5V6.5C11 9 9 11 6.5 12C4 11 2 9 2 6.5V3.5L6.5 1Z" stroke="rgba(255,255,255,.8)" strokeWidth="1.1" fill="none" /><path d="M4.5 6.5L6 8L9 5" stroke="rgba(255,255,255,.8)" strokeWidth="1.1" strokeLinecap="round" /></svg>}
                title="Insurance Included"
                sub="Every plan"
              />
              <HeroFeature
                icon={<svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5" stroke="rgba(255,255,255,.8)" strokeWidth="1.1" /><path d="M6.5 4V6.5L8 8" stroke="rgba(255,255,255,.8)" strokeWidth="1.1" strokeLinecap="round" /></svg>}
                title="Instant Activation"
                sub="No waiting period"
              />
              <HeroFeature
                icon={<svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M4 6.5L5.5 8L9 5" stroke="rgba(255,255,255,.8)" strokeWidth="1.1" strokeLinecap="round" /><circle cx="6.5" cy="6.5" r="5" stroke="rgba(255,255,255,.8)" strokeWidth="1.1" /></svg>}
                title="500+ Hospitals"
                sub="Cashless network"
              />
            </div>
          </div>
          <img src="https://affinity.medibuddy.in/assets/homepage/ic_package1.svg" alt="" className="pfc-hero-icon" />
        </div>

        <div className="pfc-plan-section">
          <div className="pfc-plan-table-wrap">
            <table className="pfc-plan-table">
              <thead>
                <tr>
                  <th style={{ width: 45 }}>S.No.</th>
                  <th style={{ width: '28%' }}>Plan Name</th>
                  <th style={{ width: '18%' }}>Coverage</th>
                  <th style={{ width: '20%' }}>Membership Fees</th>
                  <th className="center" style={{ width: 100 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((p, i) => {
                  const members = getMembers(p);
                  const price = getPrice(p);
                  return (
                    <tr
                      key={p.id || i}
                      style={{ background: i % 2 === 0 ? '#fff' : '#F9FBFC' }}
                      onClick={() => handleSelectPlan(p)}
                    >
                      <td className="sno">{i + 1}</td>
                      <td className="name">{p.title}</td>
                      <td className="coverage">{members} Member{members > 1 ? 's' : ''}</td>
                      <td className="price">
                        {formatPrice(price)}
                        <span className="per">/year</span>
                      </td>
                      <td className="center" style={{ textAlign: 'center' }}>
                        <button className="pfc-plan-select-btn" onClick={(e) => { e.stopPropagation(); handleSelectPlan(p); }}>
                          Select &#8594;
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="pfc-plan-footer-note">{footerNote}</div>
        </div>
      </div>
    );
  }

  /* ── Screen 2: Enrollment Form ── */
  if (screen === 'enrollment' && selectedPlan) {
    const plan = selectedPlan;
    const members = getMembers(plan);
    const price = getPrice(plan);
    const features = getFeatures(plan);
    const strikePrice = plan.strikePrice || '';
    const duration = 'year';

    const renderFeatures = () =>
      features.map((f, j) => {
        const isLast = j === features.length - 1;
        return (
          <div key={j} className="pfc-feature-row">
            <div className={`pfc-feature-icon blue`}>
              <CheckIcon color={'#0066DC'} />
            </div>
            <span dangerouslySetInnerHTML={{ __html: f }} />
          </div>
        );
      });

    return (
      <div className="pfc-portal">
        <PFCHeader />

        <div className="pfc-form-container">
          <div className="pfc-plan-summary">
            <div className="pfc-plan-summary-header">
              <div className="pfc-plan-summary-header-circle" />
              <div className="pfc-plan-summary-label">Selected Plan</div>
              <div className="pfc-plan-summary-name">{plan.title}</div>
              <div className="pfc-plan-summary-price">
                {formatPrice(price)}
                <span className="per">/{duration}</span>
              </div>
              <div className="pfc-plan-summary-members">
                Includes up to {members} member{members > 1 ? 's' : ''}
              </div>
              {strikePrice && (
                <div className="pfc-plan-summary-savings">
                  &#128176; Save up to {formatPrice(strikePrice)}
                </div>
              )}
            </div>
            <div className="pfc-plan-summary-body">
              <div className="pfc-plan-summary-features-label">What's Included</div>
              {renderFeatures()}
              <div className="pfc-plan-summary-back">
                <span className="pfc-back-link" onClick={handleBackToPlans}>
                  &#8592; Back to Plan Selection
                </span>
              </div>
            </div>
          </div>

          <div className="pfc-form-card">
            <div className="pfc-form-title-row">
              <div className="pfc-form-title">
                <span className="pfc-form-title-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1L14 4V8C14 11.5 11.5 14 8 15C4.5 14 2 11.5 2 8V4L8 1Z" stroke="#0066DC" strokeWidth="1.3" fill="none"/><path d="M5.5 8L7 9.5L10.5 6" stroke="#0066DC" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                Customer Enrolment
              </div>
              <div className="pfc-form-badge">
                {plan.title} &#8212; {formatPrice(price)}/{duration}
              </div>
            </div>
            <div className="pfc-form-subtitle">Fill in all details to generate the payment link for your customer</div>

            <div className="pfc-tabs" role="tablist" aria-label="Customer enrolment sections">
              <button id="pfc-tab-0" className={`pfc-tab ${activeTab === 0 ? 'active' : ''} ${completedTabs[0] ? 'completed' : ''}`} type="button" role="tab" aria-selected={activeTab === 0} aria-controls="pfc-panel-0" onClick={() => handleTabClick(0)} onKeyDown={event => handleTabKeyDown(event, 0)}>
                {completedTabs[0] && activeTab !== 0 ? <span className="pfc-tab-check">&#10003;</span> : null}
                1. Customer
              </button>
              <button id="pfc-tab-1" className={`pfc-tab ${activeTab === 1 ? 'active' : ''} ${completedTabs[1] ? 'completed' : ''}`} type="button" role="tab" aria-selected={activeTab === 1} aria-controls="pfc-panel-1" onClick={() => handleTabClick(1)} onKeyDown={event => handleTabKeyDown(event, 1)}>
                {completedTabs[1] && activeTab !== 1 ? <span className="pfc-tab-check">&#10003;</span> : null}
                2. Nominee
              </button>
              <button id="pfc-tab-2" className={`pfc-tab ${activeTab === 2 ? 'active' : ''} ${completedTabs[2] ? 'completed' : ''}`} type="button" role="tab" aria-selected={activeTab === 2} aria-controls="pfc-panel-2" onClick={() => handleTabClick(2)} onKeyDown={event => handleTabKeyDown(event, 2)}>
                {completedTabs[2] && activeTab !== 2 ? <span className="pfc-tab-check">&#10003;</span> : null}
                3. Agent
              </button>
            </div>

            {activeTab === 0 && (
              <div id="pfc-panel-0" role="tabpanel" aria-labelledby="pfc-tab-0" tabIndex={0}>
                <div className="pfc-section-label">Customer Details</div>
                <div className="pfc-field-grid">
                  <InputField label="First Name <span class='req'>*</span>" name="firstName" value={formData.firstName || ''} onChange={handleChange} onBlur={handleBlur} error={errors.firstName} placeholder="Enter first name" />
                  <InputField label="Last Name <span class='req'>*</span>" name="lastName" value={formData.lastName || ''} onChange={handleChange} onBlur={handleBlur} error={errors.lastName} placeholder="Enter last name" />
                  <DobField label="Date of Birth <span class='req'>*</span>" name="dob" value={formData.dob || ''} onChange={handleChange} onBlur={handleBlur} error={errors.dob} />
                  <SelectField label="Gender <span class='req'>*</span>" name="gender" value={formData.gender || ''} onChange={handleChange} onBlur={handleBlur} error={errors.gender} placeholder="Select gender" options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]} />
                  <InputField label="Loan Account Number (LAN) <span class='req'>*</span>" name="lan" value={formData.lan || ''} onChange={handleChange} onBlur={handleBlur} error={errors.lan} placeholder="Enter LAN" />
                  <InputField label="Mobile Number <span class='req'>*</span>" name="mobile" value={formData.mobile || ''} onChange={handleChange} onBlur={handleBlur} error={errors.mobile} prefix="+91" placeholder="10-digit number" maxLength={10} />
                  <InputField label="Email Address <span class='req'>*</span>" name="email" value={formData.email || ''} onChange={handleChange} onBlur={handleBlur} error={errors.email} placeholder="example@gmail.com" />
                  <InputField label="Pincode <span class='req'>*</span>" name="pincode" value={formData.pincode || ''} onChange={handleChange} onBlur={handleBlur} error={errors.pincode} placeholder="6-digit pincode" maxLength={6} />
                  <InputField label="City <span class='req'>*</span>" name="city" value={formData.city || ''} onChange={handleChange} onBlur={handleBlur} error={errors.city} placeholder="Enter city" />
                  <InputField label="State <span class='req'>*</span>" name="state" value={formData.state || ''} onChange={handleChange} onBlur={handleBlur} error={errors.state} placeholder="Enter state" />
                  <InputField label="Address <span class='req'>*</span>" name="address" value={formData.address || ''} onChange={handleChange} onBlur={handleBlur} error={errors.address} placeholder="Full address" fullWidth />
                </div>

                <div className="pfc-form-nav">
                  <button className="pfc-btn-primary" onClick={() => goToTab(1)}>
                    Next: Nominee &#8594;
                  </button>
                </div>
              </div>
            )}

            {activeTab === 1 && (
              <div id="pfc-panel-1" role="tabpanel" aria-labelledby="pfc-tab-1" tabIndex={0}>
                <div className="pfc-section-label">Nominee Details</div>
                <div className="pfc-field-grid">
                  <InputField label="Nominee Name <span class='req'>*</span>" name="nomineeName" value={formData.nomineeName || ''} onChange={handleChange} onBlur={handleBlur} error={errors.nomineeName} placeholder="Enter nominee name" />
                  <SelectField label="Relationship <span class='req'>*</span>" name="nomineeRelation" value={formData.nomineeRelation || ''} onChange={handleChange} onBlur={handleBlur} error={errors.nomineeRelation} placeholder="Select relationship" options={VALID_RELATIONSHIPS.map((relationship) => ({ value: relationship, label: relationship }))} />
                  <DobField label="Nominee Date of Birth <span class='req'>*</span>" name="nomineeDob" value={formData.nomineeDob || ''} onChange={handleChange} onBlur={handleBlur} error={errors.nomineeDob} />
                </div>

                {isNomineeMinor && (
                  <div>
                    <div className="pfc-section-label" style={{ marginTop: 16 }}>Guardian Details <span className="pfc-section-hint">(Required — Nominee is a minor)</span></div>
                    <div className="pfc-field-grid">
                      <InputField label="Guardian Name <span class='req'>*</span>" name="guardianName" value={formData.guardianName || ''} onChange={handleChange} onBlur={handleBlur} error={errors.guardianName} placeholder="Enter guardian name" />
                      <SelectField label="Guardian Relationship <span class='req'>*</span>" name="guardianRelation" value={formData.guardianRelation || ''} onChange={handleChange} onBlur={handleBlur} error={errors.guardianRelation} placeholder="Select relationship" options={[{ value: 'parent', label: 'Parent' }, { value: 'guardian', label: 'Legal Guardian' }, { value: 'relative', label: 'Relative' }, { value: 'other', label: 'Other' }]} />
                      <InputField label="Guardian Mobile <span class='req'>*</span>" name="guardianMobile" value={formData.guardianMobile || ''} onChange={handleChange} onBlur={handleBlur} error={errors.guardianMobile} prefix="+91" placeholder="10-digit number" maxLength={10} />
                    </div>
                  </div>
                )}

                <div className="pfc-form-nav-between">
                  <button className="pfc-btn-secondary" onClick={() => setActiveTab(0)}>
                    &#8592; Back
                  </button>
                  <button className="pfc-btn-primary" onClick={() => goToTab(2)}>
                    Next: Agent &#8594;
                  </button>
                </div>
              </div>
            )}

            {activeTab === 2 && (
              <div id="pfc-panel-2" role="tabpanel" aria-labelledby="pfc-tab-2" tabIndex={0}>
                <div className="pfc-section-label">Agent Information</div>
                <div className="pfc-field-grid">
                  <InputField label="Agent Name <span class='req'>*</span>" name="agentName" value={formData.agentName || ''} onChange={handleChange} onBlur={handleBlur} error={errors.agentName} placeholder="Enter agent name" />
                  <InputField label="Agent Email <span class='req'>*</span>" name="agentEmail" value={formData.agentEmail || ''} onChange={handleChange} onBlur={handleBlur} error={errors.agentEmail} placeholder="agent@poonawalla.com" />
                  <InputField label="Employee Code <span class='req'>*</span>" name="agentCode" value={formData.agentCode || ''} onChange={handleChange} onBlur={handleBlur} error={errors.agentCode} placeholder="Enter employee code" />
                  <InputField label="Mobile Number <span class='req'>*</span>" name="agentMobile" value={formData.agentMobile || ''} onChange={handleChange} onBlur={handleBlur} error={errors.agentMobile} prefix="+91" placeholder="10-digit number" maxLength={10} />
                  <SelectField label="Branch / Location <span class='req'>*</span>" name="branchLocation" value={formData.branchLocation || ''} onChange={handleChange} onBlur={handleBlur} error={errors.branchLocation} placeholder="Select branch / location" options={BRANCH_LOCATIONS.map(location => ({ value: location, label: location }))} />
                  <SelectField label="Vertical / Department <span class='req'>*</span>" name="verticalDepartment" value={formData.verticalDepartment || ''} onChange={handleChange} onBlur={handleBlur} error={errors.verticalDepartment} placeholder="Select vertical / department" options={VERTICAL_DEPARTMENTS.map(department => ({ value: department, label: department }))} />
                  <SelectField label="Sourcing Partner" name="sourcingPartner" value={formData.sourcingPartner || ''} onChange={handleChange} onBlur={handleBlur} error={errors.sourcingPartner} placeholder="Select sourcing partner" options={SOURCING_PARTNERS.map(partner => ({ value: partner, label: partner }))} />
                  <SelectField label="New Business" name="newBusiness" value={formData.newBusiness || ''} onChange={handleChange} onBlur={handleBlur} error={errors.newBusiness} placeholder="Select new business" options={NEW_BUSINESS_OPTIONS.map(option => ({ value: option, label: option }))} />
                </div>

                <div className="pfc-form-nav-between">
                  <button className="pfc-btn-secondary" onClick={() => setActiveTab(1)}>
                    &#8592; Back
                  </button>
                  <button className="pfc-btn-primary" onClick={handleSendConsent} disabled={!allValid || submitting}>
                    {submitting ? 'Sending...' : 'Send Consent Request \u2192'}
                  </button>
                </div>
                {submitError && <div className="pfc-field-error" style={{ marginTop: 10, textAlign: 'right' }}>{submitError}</div>}
              </div>
            )}
          </div>
        </div>

        <PFCFooter />
      </div>
    );
  }

  /* ── Screen 3: Consent Sent ── */
  const summary = submissionSummary || {};
  return (
    <div className="pfc-portal">
      <PFCHeader />

      <div className="pfc-consent-container">
        <div className="pfc-consent-success">
          <div className="pfc-success-icon-wrap">
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
              <path d="M8 22L16 30L36 12" stroke="#26935F" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="pfc-success-title">Consent Request Sent!</div>
          <div className="pfc-success-desc">
            We have sent a consent request link to the customer via SMS. They will review, accept, and complete payment to activate their membership.
          </div>
        </div>

        <div className="pfc-consent-card">
          <div className="pfc-consent-card-label">Customer Details</div>
          <div className="pfc-customer-row">
            <div className="pfc-customer-avatar">{((summary.firstName || '?').charAt(0) + (summary.lastName || '').charAt(0)).toUpperCase()}</div>
            <div>
              <div className="pfc-customer-name">{summary.firstName} {summary.lastName}</div>
              <div className="pfc-customer-phone">+91 {summary.mobile}</div>
            </div>
          </div>
          <div className="pfc-card-divider" />
          <div className="pfc-consent-card-label">What happens next?</div>
          <NextStep text="Customer receives SMS and reviews plan details" />
          <NextStep text="Accepts declarations and provides consent via OTP" />
          <NextStep text="Completes payment to activate membership instantly" />

          <div className="pfc-consent-actions">
            <button className="pfc-btn-secondary">&#128241; Resend SMS</button>
            <button className="pfc-btn-green">&#128172; Send via WhatsApp</button>
          </div>

          <div className="pfc-home-link" onClick={handleGoHome}>
            &#8592; Go to Home
          </div>
        </div>
      </div>
    </div>
  );
}

export default PFCLandingComponent;
